import { type GetServerSidePropsContext, type GetStaticPropsContext } from "next"
import { createTranslator } from "next-intl"
import { type ErrorIntlConfig } from "../types/errorIntlConfig"
import { type Namespace } from "../types/namespace"

type CreateFormatServerSideErrorCodeConfig<
    TMessages,
    TNamespace extends Namespace<TMessages>,
> = ErrorIntlConfig<TMessages, TNamespace>

type FormatServerSideErrorCodeProps = {
    context: GetServerSidePropsContext | GetStaticPropsContext
    errorCode: string
}

export function createFormatServerSideErrorCode<
    TMessages,
    TNamespace extends Namespace<TMessages>,
>({
    namespace,
    formatErrorCode,
    getMessages,
}: CreateFormatServerSideErrorCodeConfig<TMessages, TNamespace>) {
    return async function formatServerSideErrorCode({
        context,
        errorCode,
    }: FormatServerSideErrorCodeProps) {
        const locale = context.locale ?? context.defaultLocale

        if (!locale) {
            throw new Error("Locale not set: please configure i18n in Next.js config.")
        }

        try {
            const t = createTranslator({
                locale,
                messages: await getMessages(locale),
                namespace,
            })

            return formatErrorCode({ t, errorCode })
        } catch (error) {
            throw new Error(`An error occured in "formatServerSideErrorCode".`, { cause: error })
        }
    }
}
